// LMAUDITForm.js — LM Audit d'acquisition (famille EXPERTISE / CONSEIL).
// Même structure que les autres LM (entité, contact, cabinet, expert) + les
// champs propres à cette LM : « Votre projet » (§1), les 19 titres cochables
// de la nature de la mission (§2.2) et les honoraires fixes ou
// proportionnels (§2.6).
import React, { useState, useMemo, useEffect } from "react";
import { useEtatGlobal } from "../../../EtatGlobal.js";
import { User, Building2, ChevronDown, Check, Briefcase, ListChecks, Euro } from "lucide-react";
import { Input } from "../../../../components/ui/input.js";
import { Select } from "../../../../components/ui/select.js";
import { Textarea } from "../../../../components/ui/textArea.js";
import CabinetSelect from "../../components/CabinetSelect";
import SelectOuAutre from "../../components/SelectOuAutre";
import { FORME_SOCIALE_OPTIONS, CIVILITE_OPTIONS } from "../../components/lmSelectOptions";
import { AUDIT_AXES, cleLigne, lignesExclues } from "./auditAcquisitionAxes";
import { calculHonorairesAudit, montant } from "./auditHonoraires";

const rempli = (v) => v !== " " && v !== null && v !== undefined && String(v).trim() !== "";
const LABEL = "text-slate-600 mb-1 block text-xs";
const INPUT = "bg-white border-slate-200 h-9 text-sm";

function Section({ titre, icon: Icon, valide, ouvert, onToggle, children, suffixe }) {
  return (
    <div className="border-2 border-slate-200 rounded-xl bg-white overflow-hidden" style={{ padding: "10px" }}>
      <div
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        className="p-4 hover:bg-slate-50 transition-colors rounded-lg mb-2 cursor-pointer"
        onClick={onToggle}
      >
        <div style={{ display: "flex", gap: "25px", justifyContent: "center", alignItems: "center" }}>
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
              valide ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
            }`}
          >
            {valide ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
          </div>
          <h3 className="font-semibold text-slate-800" style={{ margin: 0 }}>
            {titre}
          </h3>
          {suffixe}
        </div>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${ouvert ? "rotate-180" : ""}`} />
      </div>
      {ouvert && <div className="p-6 pt-2 space-y-4">{children}</div>}
    </div>
  );
}

function LMAUDITForm({ handleInputChange, setDetailsMission, setDetailsHonoraire }) {
  const { formData, setFormData } = useEtatGlobal();
  const [ouverts, setOuverts] = useState({ entite: true, projet: true, mission: true, honoraires: true });
  const [axesOuverts, setAxesOuverts] = useState({});
  const basculer = (k) => setOuverts((o) => ({ ...o, [k]: !o[k] }));

  // Valeurs par défaut propres à cette LM (nouvelle LM ou après réinitialisation).
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      type_remuneration: prev.type_remuneration === "proportionnel" ? "proportionnel" : "fixe",
      audit_lignes_exclues: lignesExclues(prev.audit_lignes_exclues),
    }));
  }, [setFormData]);

  const exclues = useMemo(() => new Set(lignesExclues(formData.audit_lignes_exclues)), [formData.audit_lignes_exclues]);
  const totalLignes = AUDIT_AXES.reduce((s, a) => s + a.lignes.length, 0);
  const nbRetenues = totalLignes - exclues.size;

  const majExclues = (fn) =>
    setFormData((prev) => {
      const set = new Set(lignesExclues(prev.audit_lignes_exclues));
      fn(set);
      return { ...prev, audit_lignes_exclues: [...set] };
    });
  const basculerLigne = (cle) => majExclues((set) => (set.has(cle) ? set.delete(cle) : set.add(cle)));
  const basculerAxe = (i, cocher) =>
    majExclues((set) => AUDIT_AXES[i].lignes.forEach((_, j) => (cocher ? set.delete(cleLigne(i, j)) : set.add(cleLigne(i, j)))));
  const toutCocher = (cocher) =>
    setFormData((prev) => ({
      ...prev,
      audit_lignes_exclues: cocher ? [] : AUDIT_AXES.flatMap((a, i) => a.lignes.map((_, j) => cleLigne(i, j))),
    }));

  // Honoraires : totaux recalculés à chaque saisie et repris dans formData
  // (affichage des brouillons, document généré).
  const hono = useMemo(() => calculHonorairesAudit(formData), [formData]);
  useEffect(() => {
    const ht = montant(hono.ht);
    const tva = montant(hono.tva);
    const ttc = montant(hono.ttc);
    if (formData.total_honoraires === ht && formData.TVA_honoraires === tva && formData.total_honoraires_ttc === ttc) return;
    setFormData((prev) => ({ ...prev, total_honoraires: ht, TVA_honoraires: tva, total_honoraires_ttc: ttc }));
  }, [hono, formData.total_honoraires, formData.TVA_honoraires, formData.total_honoraires_ttc, setFormData]);

  const entiteValide = ["nom_client", "lieu_collab", "nom", "prenom", "email", "expert_comptable_nom"].every((c) => rempli(formData[c]));
  const projetValide = ["motifs_reprise", "secteur_activite", "cible_acquisition", "prix_achat", "modalite_financement", "calendrier_acquisition"].every(
    (c) => rempli(formData[c])
  );
  const missionValide = nbRetenues > 0 && rempli(formData.mois_intervention);
  const honorairesValide = hono.ht !== null && (!hono.proportionnel || rempli(formData.montant_minimum));

  useEffect(() => {
    setDetailsMission(entiteValide && projetValide && missionValide);
  }, [entiteValide, projetValide, missionValide, setDetailsMission]);
  useEffect(() => {
    setDetailsHonoraire(honorairesValide);
  }, [honorairesValide, setDetailsHonoraire]);

  return (
    <div className="Container space-y-4">
      {/* 1. Entité / contact */}
      <Section titre="1. Votre entité" icon={Building2} valide={entiteValide} ouvert={ouverts.entite} onToggle={() => basculer("entite")}>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={LABEL}>Forme sociale</label>
                <SelectOuAutre name="forme_sociale" value={formData.forme_sociale} onChange={handleInputChange} options={FORME_SOCIALE_OPTIONS} />
              </div>
              <div>
                <label className={LABEL}>Nom du client</label>
                <Input type="text" name="nom_client" value={formData.nom_client} onChange={handleInputChange} className={INPUT} />
              </div>
            </div>
            <CabinetSelect />
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={LABEL}>Expert-Comptable</label>
                <Select name="expert_comptable_nom" value={formData.expert_comptable_nom} onChange={handleInputChange}>
                  <option value="Samuel HOARAU">Samuel HOARAU</option>
                  <option value="Anthony HOARAU">Anthony HOARAU</option>
                </Select>
              </div>
              <div>
                <label className={LABEL}>Signataire</label>
                <Select name="expert_comptable" value={formData.expert_comptable} onChange={handleInputChange}>
                  <option value="Samuel HOARAU">Mrs Samuel HOARAU</option>
                  <option value="Anthony HOARAU">Mrs Anthony HOARAU</option>
                </Select>
              </div>
              <div>
                <label className={LABEL}>Mois d'intervention</label>
                <Input
                  type="text"
                  name="mois_intervention"
                  value={formData.mois_intervention}
                  onChange={handleInputChange}
                  placeholder="ex. novembre 2026"
                  className={INPUT}
                />
              </div>
            </div>
          </div>

          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <h4 className="font-semibold text-slate-700 mb-5 text-sm flex items-center gap-2">
              <User className="w-4 h-4 text-[#8B1D41]" />
              Contact Référent
            </h4>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>Civilité</label>
                  <SelectOuAutre name="civilite_lm" value={formData.civilite_lm} onChange={handleInputChange} options={CIVILITE_OPTIONS} />
                </div>
                <div>
                  <label className={LABEL}>Nom</label>
                  <Input type="text" name="nom" value={formData.nom} onChange={handleInputChange} className={INPUT} />
                </div>
              </div>
              <div>
                <label className={LABEL}>Prénom</label>
                <Input type="text" name="prenom" value={formData.prenom} onChange={handleInputChange} className={INPUT} />
              </div>
              <div>
                <label className={LABEL}>Email</label>
                <Input type="email" name="email" value={formData.email} onChange={handleInputChange} className={INPUT} />
              </div>
              <div>
                <label className={LABEL}>Téléphone</label>
                <Input type="text" name="telephone" value={formData.telephone} onChange={handleInputChange} className={INPUT} />
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* 2. Votre projet (§1 de la LM) */}
      <Section titre="2. Votre projet" icon={Briefcase} valide={projetValide} ouvert={ouverts.projet} onToggle={() => basculer("projet")}>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className={LABEL}>Motifs et objectifs du projet de reprise</label>
            <Textarea name="motifs_reprise" value={formData.motifs_reprise || ""} onChange={handleInputChange} rows={2} className="text-sm" />
          </div>
          <div>
            <label className={LABEL}>Secteur d'activité</label>
            <Textarea name="secteur_activite" value={formData.secteur_activite || ""} onChange={handleInputChange} rows={2} className="text-sm" />
          </div>
          <div>
            <label className={LABEL}>Cible à acquérir</label>
            <Textarea name="cible_acquisition" value={formData.cible_acquisition || ""} onChange={handleInputChange} rows={2} className="text-sm" />
          </div>
          <div>
            <label className={LABEL}>Modalité de financement</label>
            <Textarea
              name="modalite_financement"
              value={formData.modalite_financement || ""}
              onChange={handleInputChange}
              rows={2}
              className="text-sm"
            />
          </div>
          <div>
            <label className={LABEL}>Prix d'achat envisagé (€)</label>
            <Input
              type="text"
              inputMode="decimal"
              name="prix_achat"
              value={formData.prix_achat || ""}
              onChange={handleInputChange}
              placeholder="ex. 250000"
              className={INPUT}
            />
          </div>
          <div>
            <label className={LABEL}>Calendrier prévisionnel de l'acquisition</label>
            <Input
              type="text"
              name="calendrier_acquisition"
              value={formData.calendrier_acquisition || ""}
              onChange={handleInputChange}
              className={INPUT}
            />
          </div>
        </div>
      </Section>

      {/* 3. Nature et objectif de la mission (§2.2) */}
      <Section
        titre="3. Nature et objectif de la mission"
        icon={ListChecks}
        valide={missionValide}
        ouvert={ouverts.mission}
        onToggle={() => basculer("mission")}
        suffixe={
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
            {nbRetenues} / {totalLignes} lignes retenues
          </span>
        }
      >
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500 m-0">
            Tout est coché par défaut : décochez les lignes à ne pas faire apparaître. Un titre sans ligne cochée disparaît de la LM et
            les titres suivants sont renumérotés.
          </p>
          <div className="flex gap-2 shrink-0">
            <button type="button" onClick={() => toutCocher(true)} className="text-[11px] px-2.5 py-1 rounded-lg border border-slate-200 hover:bg-slate-50">
              Tout cocher
            </button>
            <button type="button" onClick={() => toutCocher(false)} className="text-[11px] px-2.5 py-1 rounded-lg border border-slate-200 hover:bg-slate-50">
              Tout décocher
            </button>
          </div>
        </div>
        <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
          {AUDIT_AXES.map((axe, i) => {
            const nbCoches = axe.lignes.filter((_, j) => !exclues.has(cleLigne(i, j))).length;
            const tous = nbCoches === axe.lignes.length;
            const ouvert = !!axesOuverts[i];
            return (
              <div key={i}>
                <div className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={tous}
                    ref={(el) => el && (el.indeterminate = nbCoches > 0 && !tous)}
                    onChange={() => basculerAxe(i, !tous)}
                    className="w-4 h-4 accent-[#8B1D41] cursor-pointer"
                    title={tous ? "Décocher tout le titre" : "Cocher tout le titre"}
                  />
                  <button
                    type="button"
                    onClick={() => setAxesOuverts((o) => ({ ...o, [i]: !o[i] }))}
                    className={`flex-1 flex items-center justify-between text-left text-[13px] font-medium ${
                      nbCoches === 0 ? "text-slate-400 line-through" : "text-slate-800"
                    }`}
                  >
                    <span>
                      {i + 1}. {axe.titre}
                    </span>
                    <span className="flex items-center gap-2 text-[11px] font-normal text-slate-500 no-underline">
                      {nbCoches}/{axe.lignes.length}
                      <ChevronDown className={`w-4 h-4 transition-transform ${ouvert ? "rotate-180" : ""}`} />
                    </span>
                  </button>
                </div>
                {ouvert && (
                  <div className="pl-10 pr-3 pb-2 space-y-1">
                    {axe.lignes.map((ligne, j) => {
                      const cle = cleLigne(i, j);
                      return (
                        <label key={cle} className="flex items-start gap-2 text-[12px] text-slate-700 cursor-pointer py-0.5">
                          <input
                            type="checkbox"
                            checked={!exclues.has(cle)}
                            onChange={() => basculerLigne(cle)}
                            className="mt-0.5 w-3.5 h-3.5 accent-[#8B1D41] cursor-pointer shrink-0"
                          />
                          <span className={exclues.has(cle) ? "text-slate-400 line-through" : ""}>{ligne}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      {/* 4. Honoraires (§2.6) */}
      <Section titre="4. Honoraires" icon={Euro} valide={honorairesValide} ouvert={ouverts.honoraires} onToggle={() => basculer("honoraires")}>
        <div>
          <label className={LABEL}>Type de rémunération</label>
          <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
            {[
              ["fixe", "Fixe"],
              ["proportionnel", "Proportionnel"],
            ].map(([v, l]) => (
              <button
                key={v}
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, type_remuneration: v }))}
                className={`px-4 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  formData.type_remuneration === v ? "bg-[#8B1D41] text-white" : "text-slate-600 hover:text-slate-800"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {hono.proportionnel ? (
            <>
              <div>
                <label className={LABEL}>Taux (% du prix d'achat)</label>
                <Input
                  type="text"
                  inputMode="decimal"
                  name="taux_honoraires"
                  value={formData.taux_honoraires || ""}
                  onChange={handleInputChange}
                  placeholder="ex. 2"
                  className={INPUT}
                />
              </div>
              <div>
                <label className={LABEL}>Montant minimum HT (€)</label>
                <Input
                  type="text"
                  inputMode="decimal"
                  name="montant_minimum"
                  value={formData.montant_minimum || ""}
                  onChange={handleInputChange}
                  className={INPUT}
                />
              </div>
            </>
          ) : (
            <div>
              <label className={LABEL}>Honoraires forfaitaires HT (€)</label>
              <Input
                type="text"
                inputMode="decimal"
                name="montant_fixe"
                value={formData.montant_fixe || ""}
                onChange={handleInputChange}
                className={INPUT}
              />
            </div>
          )}
          <div>
            <label className={LABEL}>TVA (%)</label>
            <Input
              type="text"
              inputMode="decimal"
              name="TVA_honoraires_pourcentage"
              value={formData.TVA_honoraires_pourcentage || ""}
              onChange={handleInputChange}
              className={INPUT}
            />
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm grid grid-cols-3 gap-3">
          <div>
            <p className="text-[11px] text-slate-500 m-0">Total HT{hono.proportionnel ? " (prix d'achat × taux)" : ""}</p>
            <p className="font-semibold text-slate-800 m-0">{hono.ht === null ? "—" : `${montant(hono.ht)} €`}</p>
          </div>
          <div>
            <p className="text-[11px] text-slate-500 m-0">TVA</p>
            <p className="font-semibold text-slate-800 m-0">{hono.tva === null ? "—" : `${montant(hono.tva)} €`}</p>
          </div>
          <div>
            <p className="text-[11px] text-slate-500 m-0">Total TTC</p>
            <p className="font-semibold text-slate-800 m-0">{hono.ttc === null ? "—" : `${montant(hono.ttc)} €`}</p>
          </div>
        </div>
      </Section>
    </div>
  );
}

export default LMAUDITForm;
